import { HazardScenario } from '../types/hazard.types'
import { isMeaningfulParamValue } from './hazard.constants'
import { ScenarioGridParameters } from './hazardScenarioGridParameters.utils'

export type ScenarioFilterValue = string | number | boolean

export type ActiveScenarioFilters = Record<string, ScenarioFilterValue>

/**
 * Extracts a filter map from a single scenario, scoped to the given filterable keys.
 * Skips null, undefined, and uninformative values.
 * Used for: init on load, sync on select, sync on reset.
 */
export const buildFiltersFromScenario = (
  scenario: HazardScenario,
  filterableKeys: string[],
): ActiveScenarioFilters => {
  const filters: ActiveScenarioFilters = {}
  for (const key of filterableKeys) {
    const val = scenario.parameters[key]
    if (isMeaningfulParamValue(val)) {
      filters[key] = val as ScenarioFilterValue
    }
  }
  return filters
}

/**
 * Filters a flat array of HazardScenario by the active filter selections.
 * Each filter key must match the scenario's parameter value exactly.
 * Returns all scenarios if no filters are active.
 */
export const filterHazardScenarios = (
  scenarios: HazardScenario[],
  activeFilters: ActiveScenarioFilters,
): HazardScenario[] => {
  if (Object.keys(activeFilters).length === 0) return scenarios

  return scenarios.filter((scenario) => {
    for (const [key, value] of Object.entries(activeFilters)) {
      if (scenario.parameters[key] !== value) return false
    }
    return true
  })
}

/**
 * Resolves which parameters are filterable for a given set of scenarios.
 * Only includes parameters with more than one unique meaningful value,
 * excluding the row/col keys already used as grid axes.
 */
export const resolveFilterableParameters = (
  scenarios: HazardScenario[],
  gridParams: ScenarioGridParameters,
): string[] => {
  const excluded = new Set([gridParams.rowKey, gridParams.colKey])

  const uniqueValues = new Map<string, Set<ScenarioFilterValue>>()

  for (const scenario of scenarios) {
    for (const [key, val] of Object.entries(scenario.parameters)) {
      if (excluded.has(key)) continue
      if (!isMeaningfulParamValue(val)) continue
      if (!uniqueValues.has(key)) uniqueValues.set(key, new Set())
      uniqueValues.get(key)!.add(val as ScenarioFilterValue)
    }
  }

  return [...uniqueValues.entries()].filter(([, values]) => values.size > 1).map(([key]) => key)
}

/**
 * Formats a filter value for display.
 * Booleans → 'Yes' / 'No', everything else → String.
 */
export const formatFilterValue = (value: ScenarioFilterValue): string =>
  typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)

/**
 * Resolves all unique meaningful values per filterable key, pre-sorted for display.
 * Returns a map of key → sorted values.
 */
export const resolveFilterValuesByKey = (
  scenarios: HazardScenario[],
  filterableKeys: string[],
): Record<string, ScenarioFilterValue[]> => {
  const map: Record<string, Set<ScenarioFilterValue>> = {}

  for (const key of filterableKeys) {
    map[key] = new Set<ScenarioFilterValue>()
  }

  for (const scenario of scenarios) {
    for (const key of filterableKeys) {
      const value = scenario.parameters[key]
      if (isMeaningfulParamValue(value)) {
        map[key].add(value as ScenarioFilterValue)
      }
    }
  }

  return Object.fromEntries(
    filterableKeys.map((key) => [
      key,
      [...map[key]].sort((a, b) => formatFilterValue(a).localeCompare(formatFilterValue(b))),
    ]),
  )
}
